import { Tabs as T } from "radix-ui";
import { type ComponentPropsWithoutRef, createContext, type ReactNode, useContext } from "react";
import { cn } from "../lib/cn";

type TabsVariant = "pill" | "line";
const VariantContext = createContext<TabsVariant>("pill");

export interface TabsProps extends ComponentPropsWithoutRef<typeof T.Root> {
  /** pill: libraries.dev segmented bar, for switching views. line: underline, for console pages. */
  variant?: TabsVariant;
}

export function Tabs({ variant = "pill", className, ...props }: TabsProps) {
  return (
    <VariantContext.Provider value={variant}>
      <T.Root className={cn("flex flex-col gap-3", className)} {...props} />
    </VariantContext.Provider>
  );
}

export function TabsList({ className, ...props }: ComponentPropsWithoutRef<typeof T.List>) {
  const variant = useContext(VariantContext);
  return (
    <T.List
      className={cn(
        variant === "pill"
          ? "inline-flex h-8 w-fit items-center gap-0.5 rounded-pill bg-fill-1 p-0.5"
          : "flex h-9 items-end gap-4 border-hairline border-b",
        className,
      )}
      {...props}
    />
  );
}

export interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof T.Trigger> {
  count?: number;
  icon?: ReactNode;
}

export function TabsTrigger({ className, count, icon, children, ...props }: TabsTriggerProps) {
  const variant = useContext(VariantContext);
  return (
    <T.Trigger
      className={cn(
        "inline-flex select-none items-center gap-1.5 whitespace-nowrap font-medium text-ui outline-none",
        "transition-[color,background-color,box-shadow] duration-250 ease-out [&_svg]:size-3.5",
        "disabled:pointer-events-none disabled:opacity-45",
        variant === "pill"
          ? [
              "h-7 rounded-pill px-3 text-ink-2 hover:text-ink",
              "data-[state=active]:bg-surface-3 data-[state=active]:text-ink data-[state=active]:shadow-control",
              "light:data-[state=active]:bg-surface-1",
              "focus-visible:shadow-[0_0_0_2px_var(--vd-focus)]",
            ]
          : [
              "-mb-px h-9 border-transparent border-b-2 px-0.5 text-ink-3 hover:text-ink",
              "data-[state=active]:border-ink data-[state=active]:text-ink",
              "focus-visible:text-ink focus-visible:underline",
            ],
        className,
      )}
      {...props}
    >
      {icon}
      {children}
      {count !== undefined ? (
        <span className="rounded-pill bg-fill-2 px-1.5 text-micro text-ink-3 tnum">{count}</span>
      ) : null}
    </T.Trigger>
  );
}

export function TabsContent({ className, ...props }: ComponentPropsWithoutRef<typeof T.Content>) {
  return <T.Content className={cn("outline-none", className)} {...props} />;
}
